import * as React from 'react';
import { Select, DynamicSelectIdentifier, SpinnerPage } from '@common/components';
import { hasValue } from '@common/validators';
import { LocalForm } from 'react-redux-form';
import { deepSearch } from '@common/utils';
import { TradeViewModel, ZipCountyOptionsViewModel, ZipCityViewModel, ExcludedZipViewModel } from '@models/Vendor';
import * as classes from '../../vendor-trade-maintenance.scss';
import { AddressViewModel } from '@models/common.models';
import { County } from './trade-county-component';

export interface TradeZipsComponentProperties {
  readonly currentTrade?: TradeViewModel;
  readonly isExclusion?: boolean;
  readonly loadingVendor: boolean;
  readonly readonly?: boolean;
  readonly zipsSaving?: boolean;
  readonly id: number;
  readonly address: AddressViewModel;
  readonly keyVal: string;
  readonly zipCountyOptions: ZipCountyOptionsViewModel[];
  readonly excludedZips: ExcludedZipViewModel[];
  readonly coveredZips: string[];
  readonly isTab?: boolean;

  readonly getVendorDetails: (id: number) => void;
  readonly getZipExclusions: (id: number, tradeId: number) => void;
  readonly getZipCountyOptions: (stateAbbr: string) => void;
  readonly saveChangesIncluded: (
    vendorId: number,
    includedZips: string[],
    toBeDeletedZips: string[],
    callback?: Function
  ) => void;
  readonly saveChangesExcluded: (
    tradeId: number,
    vendorId: number,
    excludedZips: ExcludedZipViewModel[],
    toBeDeletedZips: ExcludedZipViewModel[],
    callback?: Function
  ) => void;
}

export interface TradeZipsComponentState {
  readonly selectedZips: string[];
  readonly countyFilter: string;
  readonly cityZipfilter: string;
  readonly zipCountyOptions: ZipCountyOptionsViewModel[];
  readonly unsavedChanges: boolean;
  readonly stateAbbr: string;
}

export class TradeZipsComponent extends React.Component<TradeZipsComponentProperties, TradeZipsComponentState> {
  /**
   * Component for managing included / excluded zip codes for vendors
   *
   * @type {*}
   * @memberof TradeZipsComponent
   */
  timerHandle: any;
  constructor(props: TradeZipsComponentProperties) {
    super(props);
    this.state = {
      // If inclusions then prepop selected zips with coveredZips prop
      selectedZips: !this.props.isExclusion && this.props.coveredZips ? this.props.coveredZips.slice(0) : [],
      countyFilter: '',
      cityZipfilter: '',
      unsavedChanges: false,
      zipCountyOptions: [],
      // If vendor has a registered address then select the state for that address
      stateAbbr: '-1'
    };
  }

  componentDidMount() {
    // Fetch exclusion data for current trade on mount
    if (this.props.isExclusion && this.props.currentTrade) {
      this.props.getZipExclusions(this.props.currentTrade.vendorId, this.props.currentTrade.tradeId);
    }
    if (this.props.address) {
      this.setState({
        stateAbbr: this.props.address.state.abbreviation
      });
    }
    if (this.state.stateAbbr) {
      if (!this.props.zipCountyOptions) {
        // fetch zip data for selected state
        this.props.getZipCountyOptions(this.state.stateAbbr);
      } else {
        // If data already exists then load into component
        this.setState(
          {
            zipCountyOptions: []
          },
          () => this.lazyLoad(this.props.zipCountyOptions, this.props.zipCountyOptions.length)
        );
      }
    }
  }

  componentWillReceiveProps(nextProps: TradeZipsComponentProperties) {
    // Load data into component if new data is received for counties etc
    if (
      this.props.zipCountyOptions !== nextProps.zipCountyOptions ||
      (this.props.zipCountyOptions && this.state.zipCountyOptions.length === 0 && nextProps.zipCountyOptions)
    ) {
      this.setState(
        {
          zipCountyOptions: []
        },
        () => this.lazyLoad(nextProps.zipCountyOptions, nextProps.zipCountyOptions.length)
      );
    }
    // Configure selections / unsaved changes state if new selected zips data received for inclusions
    if (!this.props.isExclusion && this.props.coveredZips !== nextProps.coveredZips) {
      if (nextProps.coveredZips) {
        this.setState(
          {
            selectedZips: nextProps.coveredZips.slice(0)
          },
          () => this.setUnsavedChangesState(nextProps.coveredZips.slice(0))
        );
      }
    }
    // Configure selections / unsaved changes state if new selected zips data received for exclusions
    if (this.props.excludedZips && this.props.isExclusion && this.props.excludedZips !== nextProps.excludedZips) {
      const strippedZips = nextProps.excludedZips.map(el => el.zip);
      if (strippedZips) {
        this.setState(
          {
            selectedZips: strippedZips
          },
          () => this.setUnsavedChangesState(strippedZips)
        );
      }
    }
  }

  componentWillUpdate(nextProps: TradeZipsComponentProperties, nextState: TradeZipsComponentState) {
    // fetch zip data for selected state
    if (nextState.stateAbbr && this.state.stateAbbr !== nextState.stateAbbr) {
      this.props.getZipCountyOptions(nextState.stateAbbr);
    }
  }

  componentWillUnmount() {
    // Clear lazy load timeout on unmount
    if (this.timerHandle) {
      clearTimeout(this.timerHandle);
      this.timerHandle = 0;
    }
  }

  /**
   * Slowly loads all of the data for the dozens of accordions
   * so that app doesnt freeze with such extreme DOM changes
   *
   * @param {ZipCountyOptionsViewModel[]} zipCountyOptions
   * @param {number} max
   * @returns
   */
  lazyLoad = (zipCountyOptions: ZipCountyOptionsViewModel[], max: number) => {
    if (!zipCountyOptions || zipCountyOptions.length === 0) {
      return;
    }
    const newOne: ZipCountyOptionsViewModel = zipCountyOptions[0];
    const trimmedZips: ZipCountyOptionsViewModel[] = zipCountyOptions.slice(1);
    const prevOptions = this.state.zipCountyOptions;
    const newState: ZipCountyOptionsViewModel[] = !prevOptions.includes(newOne)
      ? [...prevOptions, newOne]
      : prevOptions;
    if (!this.props.zipCountyOptions) {
      this.timerHandle = setTimeout(() => {
        this.lazyLoad(zipCountyOptions, max);
      }, 10);
      return;
    }
    if (this.state.zipCountyOptions.length < max) {
      this.setState(
        {
          zipCountyOptions: newState
        },
        () =>
          (this.timerHandle = setTimeout(() => {
            this.lazyLoad(trimmedZips, max);
          }, 10))
      );
      return;
    }
  };

  /**
   * Render method for US state level data
   *
   * @param {ZipCountyOptionsViewModel[]} state
   * @returns
   */
  renderState = (state: ZipCountyOptionsViewModel[]) => {
    return (
      state && (
        <div className={classes.zipContainer}>
          {state.map((county: ZipCountyOptionsViewModel, index: number) => {
            return county.cities.length > 0 ? (
              this.renderCounty(county)
            ) : (
              <h3 key={`county-${index}`}>No results available for {county.countyName} County</h3>
            );
          })}
        </div>
      )
    );
  };

  /**
   * Render method for counties
   *
   * @param {ZipCountyOptionsViewModel} county
   * @returns
   */
  renderCounty = (county: ZipCountyOptionsViewModel) => {
    // Calculate numbner of selected zips in current county
    const selectedCount = county.cities.reduce((sum: number, currentValue: ZipCityViewModel) => {
      if (!this.state.selectedZips) {
        return 0;
      } else {
        return sum + currentValue.zipCodes.filter(value => this.state.selectedZips.includes(value)).length;
      }
    }, 0);
    const total = county.cities.reduce(
      (sum: number, currentValue: ZipCityViewModel) => sum + currentValue.zipCodes.length,
      0
    );

    const allSelected = selectedCount === total;
    if (selectedCount === 0 && this.props.readonly) {
      return;
    }
    return (
      deepSearch(this.state.cityZipfilter, county).length > 0 &&
      county.countyName.toLowerCase().indexOf(this.state.countyFilter.toLowerCase()) > -1 && (
        <County
          readonly={this.props.readonly}
          key={`${this.props.isExclusion ? this.props.currentTrade!.tradeId : 'vendor'}-${this.props.keyVal}-${
            county.countyName
          }`}
          county={county}
          selectedCount={selectedCount}
          allSelected={allSelected}
          countyFilter={this.state.countyFilter}
          selectedZips={this.state.selectedZips}
          toggleZips={this.toggleZips}
          cityZipFilter={this.state.cityZipfilter}
          isTab={this.props.isTab}
        />
      )
    );
  };

  /**
   * Method for toggling any number of zip selections
   *
   * @param {string[]} zips
   * @param {boolean} [groupSelect]
   * @returns
   */
  toggleZips = (zips: string[], groupSelect?: boolean) => {
    const uniqueZips = Array.from(new Set(zips));
    let updatedZips = this.state.selectedZips;
    uniqueZips.forEach(zip => {
      const isSelected = updatedZips.includes(zip);
      if (isSelected && groupSelect !== false) {
        updatedZips = updatedZips.filter(current => current !== zip);
      } else if (!isSelected) {
        updatedZips.push(zip);
      }
    });
    return this.setState(
      {
        selectedZips: updatedZips
      },
      () => this.setUnsavedChangesState()
    );
  };

  /**
   * Configure unsaved changes state
   * Used for blocking navigation if there are pending changes
   *
   * @param {string[]} [includedZips]
   */
  setUnsavedChangesState = (includedZips?: string[]) => {
    const loadedZips =
      includedZips ||
      (!this.props.isExclusion && this.props.coveredZips
        ? this.props.coveredZips.slice(0)
        : this.props.excludedZips.map(el => el.zip));
    let unsavedChanges = this.state.unsavedChanges;
    if (loadedZips) {
      unsavedChanges =
        loadedZips.length !== this.state.selectedZips.length ||
        !loadedZips.every(e => {
          const included = this.state.selectedZips.includes(e);
          return included;
        });
    }
    if (unsavedChanges !== this.state.unsavedChanges) {
      this.setState({
        unsavedChanges: unsavedChanges
      });
    }
  };

  /**
   * Save handler
   * Configures to be saved / deleted zips
   *
   */
  saveChanges = () => {
    const loadedZips = !this.props.isExclusion
      ? this.props.coveredZips.slice(0)
      : this.props.excludedZips.map(el => el.zip);
    const selectedZips = this.state.selectedZips;
    let toBeSavedZips: string[] = [];
    let toBeDeletedZips: string[] = [];
    let toBeSavedExcludedZips: ExcludedZipViewModel[] = [];
    let toBeDeletedExcludedZips: ExcludedZipViewModel[] = [];

    if (loadedZips) {
      toBeSavedZips = selectedZips.filter(e => {
        const included = loadedZips.includes(e);
        return !included;
      });

      toBeDeletedZips = loadedZips.filter(e => {
        const included = selectedZips.includes(e);
        return !included;
      });
      if (this.props.isExclusion && this.props.currentTrade) {
        toBeSavedExcludedZips = toBeSavedZips.map(el => {
          return {
            vendorId: this.props.id,
            tradeId: this.props.currentTrade ? this.props.currentTrade.tradeId : 0,
            zip: el
          };
        });
        toBeDeletedExcludedZips = toBeDeletedZips.map(el => {
          return {
            vendorId: this.props.id,
            tradeId: this.props.currentTrade ? this.props.currentTrade.tradeId : 0,
            zip: el
          };
        });
      }
    }
    const callback = () => {
      this.props.getZipCountyOptions(this.state.stateAbbr);
      this.props.getVendorDetails(this.props.id);
      if (this.props.isExclusion && this.props.currentTrade) {
        this.props.getZipExclusions(this.props.id, this.props.currentTrade.tradeId);
      }
    };
    this.props.isExclusion && this.props.currentTrade
      ? this.props.saveChangesExcluded(
          this.props.currentTrade.tradeId,
          this.props.id,
          toBeSavedExcludedZips,
          toBeDeletedExcludedZips,
          callback
        )
      : this.props.saveChangesIncluded(this.props.id, toBeSavedZips, toBeDeletedZips, callback);
  };

  /**
   * Clears all selected zips
   *
   */
  clearZips = () => {
    this.setState(
      {
        selectedZips: []
      },
      this.setUnsavedChangesState
    );
  };

  /**
   * Resets to initial state
   *
   */
  reset = () => {
    this.setState(
      {
        selectedZips: this.props.coveredZips ? this.props.coveredZips.slice(0) : []
      },
      this.setUnsavedChangesState
    );
  };

  /**
   * Renders spinner or content
   *
   * @returns
   */
  renderData = () => {
    if (this.props.loadingVendor) {
      return <SpinnerPage />;
    } else if (this.state.stateAbbr === '-1') {
      return <h2>Please select a state</h2>;
    } else if (this.state.zipCountyOptions.length === 0) {
      return <h2>There are no eligible zip codes for this state</h2>;
    }

    return this.renderState(this.state.zipCountyOptions);
  };

  render() {
    return (
      <LocalForm
        className={`form-horizontal${this.props.zipsSaving && ' ' + classes.saving}`}
        hideNativeErrors={true}
        onChange={(props: { state: { abbreviation: string } }) =>
          this.setState({ stateAbbr: props.state.abbreviation })
        }
        key={`${this.props.keyVal}-zips`}
      >
        <h2>
          {!this.props.readonly ? 'Search and Select Zip Codes' : 'View Covered Zip Codes'}
          {this.props.isExclusion && ' to Exclude'}
        </h2>
        <div className="row">
          <div className="form-control col-md-4">
            <label htmlFor="state.abbreviation">State</label>
            <Select
              model=".state.abbreviation"
              validators={{ required: hasValue }}
              identifier={DynamicSelectIdentifier.USStates}
              value={this.state.stateAbbr ? this.state.stateAbbr : '-1'}
              disabled={!this.props.readonly && this.state.unsavedChanges}
            />
            {!this.props.readonly &&
              this.state.unsavedChanges && (
                <div className="error">
                  <span className="error">You have unsaved changes</span>
                </div>
              )}
          </div>
          <div className="form-control col-md-4">
            <label htmlFor=".query">Filter by County</label>
            <input type="text" onChange={e => this.setState({ countyFilter: e.target.value })} />
          </div>
          <div className="form-control col-md-4">
            <label htmlFor=".query">Filter by City or Zip Code</label>
            <input type="text" onChange={e => this.setState({ cityZipfilter: e.target.value })} />
          </div>
        </div>
        {!this.state.zipCountyOptions ? (
          <SpinnerPage />
        ) : (
          <div className="row">
            <div className={`col-sm-12`}>
              {!this.props.readonly && (
                <p className="pointer text-right mr-10 inline-block" onClick={this.clearZips}>
                  Deselect All
                </p>
              )}
              {this.renderData()}
            </div>
          </div>
        )}
        {!this.props.readonly && (
          <div className="row text-right">
            <div className="col-sm-12">
              <button className="btn btn-secondary pull-left" onClick={this.reset}>
                Cancel
              </button>
              <button
                className="btn btn-primary pull-right"
                type="submit"
                disabled={!this.state.unsavedChanges}
                onClick={this.saveChanges}
              >
                Save
              </button>
            </div>
          </div>
        )}
      </LocalForm>
    );
  }
}



// WEBPACK FOOTER //
// ./src/vendor/_components/vendor-trade-maintenance/trade-zips/_components/trade-zips-component.tsx